<template>
  <div>
    <v-container
      class="full-height"
      fluid
    >
      <v-card>
        <v-card-title>Attendance</v-card-title>
        <v-card-subtitle>Report by Range</v-card-subtitle>
        <v-row class="px-4">
          <v-col cols="2">
            <v-autocomplete
              v-model="selectedStates"
              v-validate="'required'"
              :error-messages="errors.collect('selectedStates')"
              data-vv-name="selectedStates"  
              data-vv-as="states"
              label="States"
              chips
              hide-selected
              deletable-chips
              multiple
              :items="states"
              item-text="name"
              item-value="id"
            />
          </v-col>
          
          <v-col cols="2">
            <v-autocomplete
              v-model="selectedDepartments"
              v-validate="'required'"
              :error-messages="errors.collect('selectedDepartments')"  
              data-vv-name="selectedDepartments"
              data-vv-as="departments"
              label="Departments"
              chips
              hide-selected
              deletable-chips
              multiple
              :items="departments"
              item-text="name"
              item-value="id"
            />
          </v-col>
          <v-col cols="2">
            <v-autocomplete
              v-model="year"
              v-validate="'required'"
              hide-selected
              :items="years"
              label="Year"
              :error-messages="errors.collect('year')"  
              data-vv-name="year"
              chips
              deletable-chips
            />
          </v-col>

          <v-col cols="2">
            <v-autocomplete
              v-model="month"
              v-validate="'required'"
              hide-selected
              :error-messages="errors.collect('month')"  
              data-vv-name="month"
              label="Month"
              :items="months"
              chips
              deletable-chips
              item-text="name"
              item-value="id"
            />
          </v-col>
          <v-col cols="3">
            <v-autocomplete
              v-model="selectedOfficeLocations"
              label="Office Locations"
              chips
              hide-selected
              deletable-chips
              multiple
              :items="officeLocations"
              item-text="name"
              item-value="id"
            />
          </v-col>
          <v-col
            cols="1"
            class="ma-auto"
          >
            <v-btn
              :disabled="validation"
              :loading="downloading"
              color="primary" 
              @click="generate"
            >
              Download
            </v-btn>
          </v-col>
        </v-row>

        <v-row
          v-if="showProgress"
          class="text-center"
        >
          <v-col cols="12">
            <v-progress-circular 
              indeterminate
              color="primary"
            /> 
            <span
              v-if="!recordLength"
              class="font-weight-bold pl-3"
            >
              Preparing Report...
            </span> 
            <span
              v-else
              class="font-weight-bold pl-3"
            >
              Generating Report... ({{ currentRecord }}/{{ recordLength }})
            </span>
          </v-col>
        </v-row> 
      </v-card>
    </v-container>

    <div v-show="show">
      <table id="testTable">   
        <thead>
          <tr>
            <th
              colspan="33"
              align="center"
              style="height:50px;font-size: 18px;vertical-align:center"
            >
              <b>MONTHLY ATTENDANCE REPORT</b>
            </th>
          </tr>
        
          <tr>
            <th />
            <th style="background-color:#d9534f" />
            <th
              colspan="2"
              style="text-align:left"
            >
              No Punch In
            </th>
            <th style="background-color:#f0ad4e" />
            <th
              colspan="2"
              style="text-align:left"
            >
              Late/Early
            </th>
            <th>AL</th>
            <th
              colspan="2"
              style="text-align:left"
            >
              Annual Leave
            </th>

            <th>SL</th>
            <th
              colspan="2"
              style="text-align:left"
            >
              Sick Leave
            </th>

            <th>PL</th>
            <th
              colspan="2"
              style="text-align:left"
            >
              Paternity Leave
            </th>

            <th>UL</th>
            <th
              colspan="2"
              style="text-align:left"
            >
              Unpaid Leave
            </th>
          </tr>
          <tr>
            <th />
            <th>
              Range:
            </th>
            <th
              colspan="3"
              style="text-align:left"
            >
              1/{{ month }}/{{ year }} - {{ daysInMonth }}/{{ month }}/{{ year }}
            </th>
          </tr>
          <tr>
            <th />
            <th>Filters:</th>
            <th
              colspan="31"
              style="text-align:left"
            >
              {{ filteredStates }}{{ filteredDepartments?','+filteredDepartments:'' }}{{ filteredOfficeLocations?','+filteredOfficeLocations:'' }}
            </th>
          </tr>
          <tr>
            <th style="height:25px;vertical-align:center">
              Name
            </th>

            <th
              v-for="(x ,i) in daysInMonth"
              :key="i"
              align="center"
              style="width:65px;height:25px;vertical-align:center"
            >
              {{ i + 1 }} ({{ getDay($moment(year + '-' + month + '-' + (i+1)).day()) }})
            </th>
            
            <th style="width:85px;height:25px;vertical-align:center">
              Remarks
            </th>
          </tr> 
        </thead>

        <tbody>
          <span
            v-for="(item,i) in processedDatas"
            :key="i"
          >
            <tr>
              <td style="vertical-align:middle">
                <b>{{ item.employeeName }}</b>
              </td>

              <td
                v-for="(attendance,i) in item.data"
                :key="i"
                style="vertical-align:middle"
                align="center"
              >
                <span v-if="attendance.holiday">
                  {{ attendance.holiday }}  
                </span>
                <span v-else>
                  <span v-if="attendance.fullDay">
                    <span>{{ attendance.leave }}</span><br style="mso-data-placement:same-cell;">
                  </span>
                  <span v-else>
                    <span>{{ attendance.leave }}</span><br style="mso-data-placement:same-cell;">
                    <span
                      v-if="$moment(attendance.checkIn).format('HH:mm').toString() == '00:00'"
                      style="color:#d9534f"
                    >{{ $moment(attendance.checkIn).format('HH:mm') }}
                    </span>
                    <span
                      v-else-if="attendance.lateToOffice"
                      style="color:#f0ad4e"
                    >{{ $moment(attendance.checkIn).format('HH:mm') }}
                    </span>
                    <span v-else>
                      {{ $moment(attendance.checkIn).format('HH:mm') }}
                    </span>
                    <br style="mso-data-placement:same-cell;">
                    <span
                      v-if="$moment(attendance.checkOut).format('HH:mm').toString() == '00:00'"
                      style="color:#d9534f"
                    >{{ $moment(attendance.checkOut).format('HH:mm') }}
                    </span>
                    <span v-else>
                      {{ $moment(attendance.checkOut).format('HH:mm') }}
                    </span>
                    <br style="mso-data-placement:same-cell;">
                    <span>({{ $moment.utc($moment(attendance.checkOut,"YYYY-MM-DD HH:mm").diff($moment(attendance.checkIn,"YYYY-MM-DD HH:mm"))).format("HH:mm") }})</span><br style="mso-data-placement:same-cell;">
                    
                    <br style="mso-data-placement:same-cell;"> 

                    <span>{{ $moment(attendance.lunchOut).format('HH:mm') }}</span><br style="mso-data-placement:same-cell;">
                    <span>{{ $moment(attendance.lunchIn).format('HH:mm') }}</span><br style="mso-data-placement:same-cell;">
                    <span>({{ $moment.utc($moment(attendance.lunchIn,"YYYY-MM-DD HH:mm").diff($moment(attendance.lunchOut,"YYYY-MM-DD HH:mm"))).format("HH:mm") }})</span><br style="mso-data-placement:same-cell;">
                  </span>
                
                </span> 
              </td>
              <td />
            </tr>
            <tr>
              <th
                colspan="33 "
                style="background-color:#5b9bd5;height:5px"
              />
            </tr>
          </span>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
  import { mapState } from 'vuex'

  export default {
    inject: ['$validator'],

    data () {
      return {
        url: null,
        year: null,
        years: [],
        month: null,
        months: [
          { id: 1, name: 'January' },
          { id: 2, name: 'February' }, 
          { id: 3, name: 'March' }, 
          { id: 4, name: 'April' },
          { id: 5, name: 'May' }, 
          { id: 6, name: 'June' }, 
          { id: 7, name: 'July' },
          { id: 8, name: 'August' },
          { id: 9, name: 'September' },
          { id: 10, name: 'October' },
          { id: 11, name: 'November' },
          { id: 12, name: 'December' },
        ],
        states: [],
        selectedStates: null,
        departments: [],
        selectedDepartments: null,
        officeLocations: [],
        selectedOfficeLocations: null,
        readyToDownload: false,
        processedDatas: [],
        daysInMonth: 0,
        show: false,
        downloading: false,
        recordLength: 1,
        currentRecord: 2,
        showProgress: false,
      }
    },

    computed: {
      ...mapState({ employee: 'currentEmployee' }),

      validation () {
        if (!this.year || !this.month || !this.selectedStates || !this.selectedDepartments) return true
        return false
      },
      filteredStates () {
        if (!this.selectedStates || this.selectedStates.length < 1) return
        let stateNames = []
        let states = this.states.filter(state => {
          return this.selectedStates.includes(state.id)
        })

        states.forEach(state => {
          stateNames.push(state.name)
        })

        return stateNames.toString()
      },
      filteredDepartments () {
        if (!this.selectedDepartments || this.selectedDepartments.length < 1) return
        let departmentNames = []

        let departments = this.departments.filter(department => {
          return this.selectedDepartments.includes(department.id)
        })

        departments.forEach(department => {
          departmentNames.push(department.name)
        })

        return departmentNames.toString()
      },
      filteredOfficeLocations () {
        if (!this.selectedOfficeLocations || this.selectedOfficeLocations.length < 1) return
        let officeNames = []

        let officeLocations = this.officeLocations.filter(officeLocation => {
          return this.selectedOfficeLocations.includes(officeLocation.id)
        })

        officeLocations.forEach(officeLocation => {
          officeNames.push(officeLocation.name)
        })

        return officeNames.toString()
      },
    },

    watch: {
      processedDatas: function (value) {
        if (value.length > 0) {
          this.$nextTick(() => {
            this.tableToExcel('testTable', 'Attendance')
            this.readyToDownload = false 
            this.processedDatas = []
            this.showProgress = false
          })
        }
      },
    },

    created () {
      this.loadData()
    },

    methods: {  
      tableToExcel (table, name) {
        let uri = 'data:application/vnd.ms-excel;base64,'
        let template = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>{worksheet}</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--><meta http-equiv="content-type" content="text/plain; charset=UTF-8"/></head><body><table>{table}</table></body></html>'
        let base64 = function (s) { return window.btoa(unescape(encodeURIComponent(s))) }
        let format = function (s, c) { return s.replace(/{(\w+)}/g, function (m, p) { return c[p] }) }

        if (!table.nodeType) table = document.getElementById(table)
            
        let ctx = { worksheet: name || 'Worksheet', table: table.innerHTML }
        window.location.href = uri + base64(format(template, ctx))
        this.downloading = false
        
        return window.location.href
      },

      loadData () {
        Promise.all([
          this.$api.adminStates().list(this.employee.employee_uid),
          this.$api.departments().list(),
          this.$api.states().list(),
          this.$api.officeLocations().list(),
        ])
          .then(([adminStates, departments, states, officeLocations]) => {
            let departmentIds = []
            let stateIds = []

            adminStates.data.forEach(item => {
              stateIds.push(item.state_id)
              departmentIds.push(item.department_id)
            })

            this.states = _.orderBy(states.data.filter(state => {
              return stateIds.includes(state.id)
            }), ['name', ['asc']])
            
            this.departments = _.orderBy(departments.data.filter(department => {
              return departmentIds.includes(department.id)
            }), ['name', ['asc']])
            
            this.officeLocations = _.orderBy(officeLocations.data, ['name'], ['asc'])
            this.$api.employeeClockInSessions().listClockInSessionYear()
              .then(res => {
                this.years = res.data
              })
          })
      },
      generate () {
        this.currentRecord = 0 
        this.recordLength = 0
        this.downloading = true
        this.showProgress = true

        this.$api.reports().generateAttendanceReport({
          year: this.year,
          month: this.month,
          department: this.selectedDepartments,
          office: this.selectedOfficeLocations,
          state: this.selectedStates,
        }).then(res => {
          if (res.data.length < 1) {
            this.$snack({ color: 'error', message: 'No Record Found' })
            this.downloading = false
          }

          if (res.data.length > 0) {
            this.recordLength = res.data.length
            this.daysInMonth = this.$moment(`${this.year}-${this.month}`).daysInMonth()

            _.orderBy(res.data, ['city.state.name', 'office_location.name', 'person.name'], ['asc', 'asc', 'asc']).forEach(item => {
              this.processedDatas.push({
                employeeName: item.person.name,
                departmentName: item.department.name,
                officeName: item.office_location.name,
                data: this.formatData(item),
              })
              this.currentRecord++
            })
            if (this.processedDatas.length > 0) {
              this.readyToDownload = true
            }
          }
        })
      },
      formatData (item) {
        item.employee_clock_in_sessions.forEach(clockInSession => {
          clockInSession.date = this.$moment(clockInSession.created_at, 'YYYY-MM-DD').format('YYYY-MM-DD')
        })
        let clockInSessions = _.groupBy(item.employee_clock_in_sessions, 'date')
        let data = []
        const daysInMonth = this.$moment(`${this.year}-${this.month}`).daysInMonth()
        for (let i = 1; i <= daysInMonth; i++) {
          data.push({
            checkIn: this.getCheckInTime(clockInSessions[this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(i)], i),
            checkOut: this.getCheckOutTime(clockInSessions[this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(i)], i),
            lateToOffice: this.checkLate(clockInSessions[this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(i)], i, item),
            lunchOut: this.getLunchOutTime(clockInSessions[this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(i)], i, item),
            lunchIn: this.getLunchInTime(clockInSessions[this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(i)], i, item),
            holiday: this.getHoliday(item, i),
            fullDay: this.checkFullDayLeave(item, i),
            leave: this.getLeave(item, i),
          })
        }
    
        return data
      },
      getCheckInTime (clockInSessions, day) {
        if (clockInSessions) {
          return this.$moment(clockInSessions[0].created_at, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm')
        }
        return this.$moment(this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(day) + ' 00:00:00', 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm')
      },
      getCheckOutTime (clockInSessions, day) {
        if (clockInSessions) {
          return this.$moment(clockInSessions[(clockInSessions.length) - 1].created_at, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm')
        }
        
        return this.$moment(this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(day) + ' 00:00:00', 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm')
      },

      getLunchOutTime (clockInSessions, day, item) {
        let tempClockInSessions = []
        let lunchRangeStart = item.work_days[this.$moment(this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(day)).isoWeekday() - 1].work_session.lunch_range_from
        let lunchRangeEnd = item.work_days[this.$moment(this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(day)).isoWeekday() - 1].work_session.lunch_range_to

        if (clockInSessions) {
          tempClockInSessions = clockInSessions.filter(employeeClockInSession => {
            return (this.$moment(employeeClockInSession.created_at, 'YYYY-MM-DD HH:mm').isAfter(this.$moment(this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(day) + ' ' + lunchRangeStart, 'YYYY-MM-DD HH:mm'))) &&
              (this.$moment(employeeClockInSession.created_at, 'YYYY-MM-DD HH:mm').isBefore(this.$moment(this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(day) + ' ' + lunchRangeEnd, 'YYYY-MM-DD HH:mm')))
          })
        }

        if (tempClockInSessions.length > 0) {
          return this.$moment(tempClockInSessions[0].created_at, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm')
        }

        return this.$moment(this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(day) + ' 00:00:00', 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm')
      },
      getLunchInTime (clockInSessions, day, item) {
        let tempClockInSessions = []

        let lunchRangeStart = item.work_days[this.$moment(this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(day)).isoWeekday() - 1].work_session.lunch_range_from
        let lunchRangeEnd = item.work_days[this.$moment(this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(day)).isoWeekday() - 1].work_session.lunch_range_to
        
        if (clockInSessions) {
          tempClockInSessions = clockInSessions.filter(employeeClockInSession => {
            return (this.$moment(employeeClockInSession.created_at, 'YYYY-MM-DD HH:mm').isAfter(this.$moment(this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(day) + ' ' + lunchRangeStart, 'YYYY-MM-DD HH:mm'))) &&
              (this.$moment(employeeClockInSession.created_at, 'YYYY-MM-DD HH:mm').isBefore(this.$moment(this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(day) + ' ' + lunchRangeEnd, 'YYYY-MM-DD HH:mm')))
          })
        }

        if (tempClockInSessions.length > 0) {
          return this.$moment(tempClockInSessions[tempClockInSessions.length - 1].created_at, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm')
        }

        return this.$moment(this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(day) + ' 00:00:00', 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm')
      },
      getHoliday (item, day) {
        let holidays = item.city.state.holidays
        let isHoliday = holidays.find(holiday => {
          return holiday.date === (this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(day))
        })
        if (isHoliday) return 'Holiday'

        return ''
      },
      getLeave (item, day) {
        let session = null
        let leaveApplications = item.leave_applications
        leaveApplications.map(leaveApplication => {
          leaveApplication.leave_days.map(leaveDay => {
            if (leaveDay.date === (this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(day))) {
              session = leaveDay.leave_session.name + '(' + this.getLeaveType(leaveApplication.leave_entitlement.leave_type.name.toUpperCase()) + ')'
            }
          })
        })

        return session
      },
      checkLate (clockInSessions, day, item) {
        let workTime = item.work_days[this.$moment(this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(day)).isoWeekday() - 1].work_session.start_time
        
        if (item.work_days[this.$moment(this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(day)).isoWeekday() - 1].work_session.start_time === item.work_days[this.$moment(this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(day)).isoWeekday() - 1].work_session.end_time) {
          return false
        }
        
        let inTime = null
        if (clockInSessions) {
          inTime = clockInSessions[0].created_at
        }
      
        return this.$moment(inTime, 'YYYY-MM-DD HH:mm').isAfter(this.$moment(this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(day) + ' ' + workTime, 'YYYY-MM-DD HH:mm'))
      },
      checkFullDayLeave (item, day) {
        let fullDay = false
        let leaveApplications = item.leave_applications
        leaveApplications.map(leaveApplication => {
          leaveApplication.leave_days.map(leaveDay => {
            if (leaveDay.date === (this.year + '-' + this.formatValue(this.month) + '-' + this.formatValue(day))) {
              if (leaveDay.leave_session.name.toUpperCase().includes('FULL')) {
                fullDay = true
              }
            }
          })
        })

        return fullDay
      },
      getDay (i) {
        if (i === 1) {
          return 'Mon'
        } else if (i === 2) {
          return 'Tue'
        } else if (i === 3) {
          return 'Wed'
        } else if (i === 4) {
          return 'Thu'
        } else if (i === 5) {
          return 'Fri'
        } else if (i === 6) {
          return 'Sat'
        }
        return 'Sun' 
      },
      formatValue (month) {
        if (month === 1) {
          return '01'
        } else if (month === 2) {
          return '02'
        } else if (month === 3) {
          return '03'
        } else if (month === 4) {
          return '04'
        } else if (month === 5) {
          return '05'
        } else if (month === 6) {
          return '06'
        } else if (month === 7) {
          return '07'
        } else if (month === 8) {
          return '08'
        } else if (month === 9) {
          return '09'
        } else {
          return month
        }
      },
      getLeaveType (name) {
        if (name === 'ANNUAL LEAVE') {
          return 'AL'
        } else if (name === 'PATERNITY LEAVE') {
          return 'PL'
        } else if (name === 'SICK LEAVE') {
          return 'SL'
        } else if (name === 'UNPAID LEAVE') {
          return 'UL'
        }
      },
    },
  }
</script>
